import React, { useState, useEffect, useRef } from 'react';
import Reveal from './Reveal';

// OVR Cup - interactive format walkthrough (10 teams · 2 pools · playoffs).
// Players swipe/tab through Pool Play → Playoffs → QFs → Semis → Final.
// At each stage the bracket below updates to show which slots are live.

// Per-event copy for the walkthrough header. Keyed by season id.
export const CUP_INFO = {
  szn6: {
    eyebrow: 'OVR · SEASONAL CUP',
    title: 'OVR CUP', titleEm: 'SZN VI',
    blurb: 'The premium OVR event of the season.',
    details: [['Date', 'Sun Jun 28 · 12 – 8 PM'], ['Venue', 'Norm Fenn Gym · Carleton'], ['Prize', '$500 cash']],
  },
  carleton: {
    eyebrow: 'OVR × CARLETON · FUNDRAISER',
    title: 'OVR ×', titleEm: 'CARLETON',
    blurb: 'A co-ed fundraiser tournament with Carleton.',
    details: [['Date', 'Sun Oct 25 · 1 – 9 PM'], ['Venue', 'Norm Fenn Gym · Carleton'], ['Prizes', 'For the winners']],
  },
};
export const DEFAULT_CUP = 'carleton';

const STAGES = [
  { id: 'pool',     label: 'POOL PLAY',     short: '01' },
  { id: 'qualify',  label: 'QUALIFY',       short: '02' },
  { id: 'qf',       label: 'QUARTERS',      short: '03' },
  { id: 'sf',       label: 'SEMIS',         short: '04' },
  { id: 'final',    label: 'THE FINAL',     short: '05' },
];

// QF matchups
const QF = [
  { id: 'QF1', left: 'A1', right: 'B4', winnerKey: 'W(QF1)' },
  { id: 'QF2', left: 'A2', right: 'B3', winnerKey: 'W(QF2)' },
  { id: 'QF3', left: 'B1', right: 'A4', winnerKey: 'W(QF3)' },
  { id: 'QF4', left: 'B2', right: 'A3', winnerKey: 'W(QF4)' },
];

const RED = '#9D1515';

export default function OvrCupInfoModal({ open, onClose, event }) {
  const [stageIdx, setStageIdx] = useState(0);
  // Remember which way the player moved so the stage panel slides in from that side
  const prevIdx = useRef(0);
  const dir = stageIdx >= prevIdx.current ? 1 : -1;
  useEffect(() => { prevIdx.current = stageIdx; }, [stageIdx]);
  useEffect(() => { if (open) setStageIdx(0); }, [open]);
  if (!open) return null;
  const stage = STAGES[stageIdx];
  const ev = event || CUP_INFO[DEFAULT_CUP];

  const wrap = {
    position: 'fixed', inset: 0, zIndex: 1400, // above the hamburger/cart cluster so ✕ is tappable on mobile
    // No backdrop blur: this overlay is the scroll container, so a full-screen blur has to be
    // recomposited every scroll frame (~22fps without GPU raster). Slightly darker instead.
    background: 'rgba(0,0,0,0.92)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: 24, overflowY: 'auto',
  };
  const modal = {
    background: '#0a0a0a',
    border: `2px solid ${RED}`,
    maxWidth: 980, width: '100%',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    marginTop: 20, marginBottom: 60,
  };
  const hero = {
    background: `linear-gradient(135deg, ${RED} 0%, #6a0e0e 100%)`,
    padding: '40px 32px 32px',
    position: 'relative',
  };
  const closeBtn = {
    position: 'absolute', top: 14, right: 14, zIndex: 2,
    background: 'rgba(0,0,0,0.4)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
    width: 36, height: 36, cursor: 'pointer', fontSize: 18, fontWeight: 700,
  };
  const sectionWrap = { padding: '28px 32px' };

  // Stage tab pills
  const tabBar = (
    <div className="ovrcup-tabs" style={{ padding: '20px 24px 0', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid #1f1f1f', paddingBottom: 18 }}>
      {STAGES.map((s, i) => {
        const active = i === stageIdx;
        const done = i < stageIdx;
        return (
          <button
            key={s.id}
            className="ovrcup-tab-btn"
            onClick={() => setStageIdx(i)}
            style={{
              flex: '1 1 130px',
              background: active ? RED : 'transparent',
              color: active ? '#fff' : (done ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.4)'),
              border: `1px solid ${active ? RED : (done ? 'rgba(157,21,21,0.4)' : '#2a2a2a')}`,
              padding: '10px 12px',
              fontFamily: "'Bebas Neue','Oswald',sans-serif",
              fontSize: 13, letterSpacing: '0.12em',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1)',
              transform: active ? 'translateY(-2px)' : 'none',
              boxShadow: active ? '0 8px 20px rgba(157,21,21,0.35)' : 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
            }}
          >
            <span style={{ fontSize: 9, opacity: 0.6 }}>{s.short}</span>
            <span>{s.label}</span>
          </button>
        );
      })}
    </div>
  );

  // Mini swipe arrows
  const nav = (
    <div className="ovrcup-nav" style={{ padding: '0 32px 8px', display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
      <button
        onClick={() => setStageIdx(i => Math.max(0, i - 1))}
        disabled={stageIdx === 0}
        style={{
          background: 'transparent', border: '1px solid #2a2a2a', color: stageIdx === 0 ? '#333' : '#fff',
          padding: '8px 14px', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em',
          cursor: stageIdx === 0 ? 'not-allowed' : 'pointer',
        }}
      >← BACK</button>
      <button
        onClick={() => setStageIdx(i => Math.min(STAGES.length - 1, i + 1))}
        disabled={stageIdx === STAGES.length - 1}
        style={{
          background: stageIdx === STAGES.length - 1 ? 'transparent' : RED,
          border: 'none', color: stageIdx === STAGES.length - 1 ? '#333' : '#fff',
          padding: '8px 14px', fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 12, letterSpacing: '0.14em',
          cursor: stageIdx === STAGES.length - 1 ? 'not-allowed' : 'pointer',
        }}
      >NEXT →</button>
    </div>
  );

  return (
    <div style={wrap} onClick={onClose} className="ovr-anim-fade">
      <style>{`
        .ovrcup-modal { animation: ovr-pop 0.55s cubic-bezier(0.22,1,0.36,1) both; }
        .ovrcup-hero > :not(button) { animation: ovr-rise 0.6s cubic-bezier(0.22,1,0.36,1) both; }
        .ovrcup-hero > :nth-child(2) { animation-delay: 0.12s; }
        .ovrcup-hero > :nth-child(3) { animation-delay: 0.2s; }
        .ovrcup-hero > :nth-child(4) { animation-delay: 0.28s; }
        .ovrcup-hero > :nth-child(5) { animation-delay: 0.36s; }
        .ovrcup-glance > * { animation: ovr-pop 0.5s cubic-bezier(0.22,1,0.36,1) both; }
        .ovrcup-glance > :nth-child(1) { animation-delay: 0.3s; }
        .ovrcup-glance > :nth-child(2) { animation-delay: 0.38s; }
        .ovrcup-glance > :nth-child(3) { animation-delay: 0.46s; }
        .ovrcup-glance > :nth-child(4) { animation-delay: 0.54s; }
        .ovrcup-stage-rows > * { animation: var(--ovrcup-slide) 0.45s cubic-bezier(0.22,1,0.36,1) both; }
        .ovrcup-stage-rows > :nth-child(2) { animation-delay: 0.07s; }
        .ovrcup-stage-rows > :nth-child(3) { animation-delay: 0.14s; }
        .ovrcup-stage-rows > :nth-child(4) { animation-delay: 0.21s; }
        .ovrcup-slot { transition: background-color 0.35s ease, border-color 0.35s ease, color 0.35s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1); }
        .ovrcup-slot.is-live { box-shadow: 0 0 16px rgba(157,21,21,0.45); transform: scale(1.03); }
        .ovrcup-slot.is-gold { box-shadow: 0 0 18px rgba(255,215,0,0.3); transform: scale(1.05); }
        .ovrcup-check { display: inline-block; animation: ovr-check-in 0.35s cubic-bezier(0.34,1.56,0.64,1) both; }
        .ovrcup-trophy { display: inline-block; animation: ovr-trophy 0.8s cubic-bezier(0.34,1.56,0.64,1) both, ovr-float 3s ease-in-out 0.8s 2; }
        .ovrcup-list li { transition: background-color 0.25s ease; }
        .ovrcup-list li:hover { background: rgba(157,21,21,0.06); }
        @media (max-width: 640px) {
          .ovrcup-hero { padding: 28px 18px 22px !important; }
          .ovrcup-hero h1 { font-size: 46px !important; }
          .ovrcup-section { padding: 18px 16px !important; }
          .ovrcup-tabs { padding: 14px 14px 0 !important; }
          .ovrcup-tab-btn { flex: 1 1 calc(50% - 6px) !important; font-size: 11px !important; padding: 8px 10px !important; }
          .ovrcup-stage-panel { padding: 16px 16px 0 !important; }
          .ovrcup-bracket-wrap { padding: 16px 16px 0 !important; }
          .ovrcup-nav { padding: 0 16px 6px !important; }
          .ovrcup-close-cta { padding: 16px 16px 28px !important; }
          .ovrcup-close-cta button { padding: 12px 24px !important; font-size: 14px !important; }
        }
      `}</style>
      <div style={modal} className="ovrcup-modal" onClick={e => e.stopPropagation()}>
        <div style={hero} className="ovrcup-hero">
          <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>
          <div style={{ fontSize: 11, letterSpacing: '0.24em', color: 'rgba(255,255,255,0.8)', fontWeight: 700, marginBottom: 14 }}>
            {ev.eyebrow}
          </div>
          <h1 style={{
            fontFamily: "'Bebas Neue','Oswald',sans-serif",
            fontSize: 'clamp(48px, 8vw, 84px)', letterSpacing: '0.04em',
            lineHeight: 0.95, margin: 0, textShadow: '0 4px 16px rgba(0,0,0,0.4)',
          }}>
            {ev.title} <em style={{ fontStyle: 'italic' }}>{ev.titleEm}</em>
          </h1>
          <div style={{ fontSize: 14, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', marginTop: 14, fontWeight: 600 }}>
            10 Teams · 2 Pools · One Champion
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 22px', marginTop: 18 }}>
            {ev.details.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>{k}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <Reveal style={sectionWrap} className="ovrcup-section">
          <p style={{ fontSize: 15, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)', margin: 0 }}>
            {ev.blurb} <strong style={{ color: '#fff' }}>Pool play, single-elimination playoffs,
            cross-pool seeding for fair brackets.</strong> Tap through each stage below to see exactly how your team's
            journey unfolds.
          </p>
        </Reveal>

        <div style={sectionWrap} className="ovrcup-section">
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 24, color: '#fff', letterSpacing: '0.1em', borderBottom: `1px solid ${RED}`, paddingBottom: 8, margin: '0 0 14px' }}>
            AT A GLANCE
          </h2>
          <div className="ovrcup-glance" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {[
              ['Format', '10 Teams'],
              ['Pools', '2 × 5'],
              ['Playoffs', 'Single Elim'],
              ['Team of 6', '$240'],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#131313', border: '1px solid #2a2a2a', borderLeft: `3px solid ${RED}`, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, letterSpacing: '0.18em', color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>{k}</div>
                <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 24, letterSpacing: '0.04em', color: '#fff' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {tabBar}

        {/* Stage detail panel */}
        <div className="ovrcup-stage-panel" style={{ padding: '24px 32px 0' }}>
          {/* keyed by stage so rows re-animate, sliding from the side the player moved toward */}
          <div key={stage.id} className="ovrcup-stage-rows-wrap" style={{ '--ovrcup-slide': dir > 0 ? 'ovr-slide-in' : 'ovr-slide-in-rev' }}>
            <StagePanel stage={stage} stageIdx={stageIdx} />
          </div>
        </div>

        {/* Bracket visual */}
        <div className="ovrcup-bracket-wrap" style={{ padding: '24px 32px 0' }}>
          <Bracket stageIdx={stageIdx} />
        </div>

        {nav}

        <Reveal className="ovrcup-section" style={sectionWrap}>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.1em', borderBottom: `1px solid ${RED}`, paddingBottom: 8, margin: '0 0 14px' }}>
            PRICING
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} className="blitz-table">
              <tbody>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff', width: 160 }}>First 6 players</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$40 each ($240 base)</td></tr>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff' }}>Players 7 & 8</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$20 each (half price)</td></tr>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff' }}>Team of 6</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$240 total</td></tr>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff' }}>Team of 7</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$260 total</td></tr>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff' }}>Team of 8</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$280 total</td></tr>
                <tr><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', fontWeight: 600, color: '#fff' }}>Free agent</td><td style={{ padding: '10px 12px', borderBottom: '1px solid #1f1f1f', color: 'rgba(255,255,255,0.9)' }}>$40 - paid in full upfront, auto-refund if not placed</td></tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal className="ovrcup-section" style={sectionWrap}>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.1em', borderBottom: `1px solid ${RED}`, paddingBottom: 8, margin: '0 0 14px' }}>
            RULES
          </h2>
          <ul className="ovrcup-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              ['Pool play', 'Single set, first to 25 points. 25-minute time cap. No win-by-2.'],
              ['Quarter Finals', '1 set · first to 25 · no time cap.'],
              ['Semis & Final', 'Best of 3 sets · 25 / 25 / 15 · win by 2, capped at 30 · no time limit.'],
              ['Standings', '2 pts per win. Ties broken by head-to-head → sets won → point differential.'],
              ['Seeding', 'Top 4 from each pool advance. Cross-pool QF matchups prevent early rematches.'],
              ['Bronze', 'Semifinal losers play for third place. Best of 3, same format as the semis.'],
              ['Rotation, libero, subs', 'Standard FIVB + OVR house rules.'],
              ['Co-ed', 'Minimum 2 women on the court at all times.'],
            ].map(([label, text]) => (
              <li key={label} style={{ padding: '10px 0', borderBottom: '1px solid #1f1f1f', fontSize: 13, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 12 }}>
                <span style={{ color: RED, fontWeight: 700, flexShrink: 0 }}>·</span>
                <span><strong style={{ color: '#fff' }}>{label}:</strong> {text}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="ovrcup-section" style={sectionWrap}>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.1em', borderBottom: `1px solid ${RED}`, paddingBottom: 8, margin: '0 0 14px' }}>
            CANCELLATION POLICY
          </h2>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)', margin: 0 }}>
            <strong style={{ color: '#fff' }}>Teams</strong> must cancel at least <strong style={{ color: '#fff' }}>5 days</strong> before the tournament start time for a full refund.
            <strong style={{ color: '#fff' }}> Free agents</strong> must cancel at least <strong style={{ color: '#fff' }}>2 days</strong> before the tournament start time for a full refund.
            All refunds are minus payment processing fees. <strong style={{ color: '#fff' }}>Free agents not placed on a team</strong> in time are refunded automatically.
          </p>
        </Reveal>

        <Reveal className="ovrcup-section" style={{ ...sectionWrap, paddingTop: 20 }}>
          <h2 style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 22, color: '#fff', letterSpacing: '0.1em', borderBottom: `1px solid ${RED}`, paddingBottom: 8, margin: '0 0 14px' }}>
            WHY THIS FORMAT
          </h2>
          <ul className="ovrcup-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              ['Fair', 'Top teams don\'t meet too early. Cross-pool seeding keeps the bracket balanced.'],
              ['Fast', 'Time-capped pool matches keep the day moving and on schedule.'],
              ['Fun', 'Every team plays at least 4 matches; more if they advance.'],
              ['Hype', 'Single-elimination playoffs build momentum. Every set matters.'],
            ].map(([label, text]) => (
              <li key={label} style={{ padding: '10px 0', borderBottom: '1px solid #1f1f1f', fontSize: 13, color: 'rgba(255,255,255,0.85)', display: 'flex', gap: 12 }}>
                <span style={{ color: RED, fontWeight: 700, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: '0.08em', minWidth: 50 }}>{label}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <div className="ovrcup-close-cta" style={{ padding: '20px 32px 40px', textAlign: 'center' }}>
          <button onClick={onClose} className="ovr-shine" style={{
            background: RED, color: '#fff', border: 'none',
            fontFamily: "'Bebas Neue','Oswald',sans-serif",
            fontSize: 16, letterSpacing: '0.18em', padding: '14px 36px', cursor: 'pointer',
          }}>
            GOT IT · TAKE ME BACK
          </button>
        </div>
      </div>
    </div>
  );
}

function StagePanel({ stage }) {
  const cardWrap = { display: 'flex', flexDirection: 'column', gap: 10 };
  const rowsCls = 'ovrcup-stage-rows';
  const row = { background: '#111', border: '1px solid #1f1f1f', borderLeft: `3px solid ${RED}`, padding: '12px 16px' };
  const rowTitle = { fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 14, letterSpacing: '0.08em', color: '#fff', marginBottom: 4 };
  const rowText = { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55 };

  if (stage.id === 'pool') {
    return (
      <div style={cardWrap} className={rowsCls}>
        <div style={row}>
          <div style={rowTitle}>10 TEAMS · 2 POOLS</div>
          <div style={rowText}>Teams are split into Pool A and Pool B (5 teams each). Each pool runs on its own court.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>4 MATCHES EACH</div>
          <div style={rowText}>Every team plays every other team in its pool once. Round robin within the pool.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>MATCH FORMAT</div>
          <div style={rowText}>1 set to 25 points · 25-minute time cap · no win-by-2 in pool play.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>STANDINGS</div>
          <div style={rowText}>Tiebreakers in order: (1) head-to-head, (2) sets won, (3) point differential.</div>
        </div>
      </div>
    );
  }

  if (stage.id === 'qualify') {
    return (
      <div style={cardWrap} className={rowsCls}>
        <div style={row}>
          <div style={rowTitle}>TOP 4 FROM EACH POOL</div>
          <div style={rowText}>Pool A places 1–4 advance. Pool B places 1–4 advance. 8 teams move on to the playoffs.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>5TH PLACE ELIMINATED</div>
          <div style={rowText}>Bottom teams in each pool are eliminated after pool play. No consolation round.</div>
        </div>
      </div>
    );
  }

  if (stage.id === 'qf') {
    return (
      <div style={cardWrap} className={rowsCls}>
        <div style={row}>
          <div style={rowTitle}>CROSS-POOL SEEDING</div>
          <div style={rowText}>Pool A's best plays Pool B's worst of the qualified teams (and vice versa). Avoids early rematches.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>MATCHUPS</div>
          <div style={rowText}>QF1: A1 vs B4 · QF2: A2 vs B3 · QF3: B1 vs A4 · QF4: B2 vs A3</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>FORMAT</div>
          <div style={{ ...rowText, whiteSpace: 'nowrap' }}>1 set · first to 25 · no time cap.</div>
        </div>
      </div>
    );
  }

  if (stage.id === 'sf') {
    return (
      <div style={cardWrap} className={rowsCls}>
        <div style={row}>
          <div style={rowTitle}>SEMIFINAL A</div>
          <div style={rowText}>Winner of QF1 vs Winner of QF4. Bracket halves are split so pool-mates can't meet again until the final.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>SEMIFINAL B</div>
          <div style={rowText}>Winner of QF2 vs Winner of QF3. Winners play for the cup, losers play for bronze.</div>
        </div>
        <div style={row}>
          <div style={rowTitle}>SAME FORMAT</div>
          <div style={{ ...rowText, whiteSpace: 'nowrap' }}>Best of 3 sets · 25 / 25 / 15 · win by 2, capped at 30.</div>
        </div>
      </div>
    );
  }

  // final
  return (
    <div style={cardWrap} className={rowsCls}>
      <div style={{ ...row, borderLeftColor: '#FFD700', background: 'linear-gradient(90deg, rgba(255,215,0,0.08), transparent)' }}>
        <div style={{ ...rowTitle, color: '#FFD700' }}>THE FINAL</div>
        <div style={rowText}>Winner of SF A vs Winner of SF B. One match. One champion.</div>
      </div>
      <div style={row}>
        <div style={rowTitle}>FORMAT</div>
        <div style={rowText}>Best of 3 sets. 25 / 25 / 15. Win by 2, capped at 30.</div>
      </div>
      <div style={row}>
        <div style={rowTitle}>BRONZE MATCH</div>
        <div style={rowText}>The two semifinal losers play for third place, same best-of-3 format.</div>
      </div>
      <div style={row}>
        <div style={rowTitle}>CEREMONY</div>
        <div style={rowText}>Awards and prizes immediately after the final. Team photo, MVP recognition.</div>
      </div>
    </div>
  );
}

function Bracket({ stageIdx }) {
  // Highlight rules:
  // stage 0 (pool): pools highlighted
  // stage 1 (qualify): pools dimmed, qualified slots glowing
  // stage 2 (qf): QF matches glowing
  // stage 3 (sf): QF dimmed, SF glowing
  // stage 4 (final): final glowing
  const showPools     = true;
  const poolActive    = stageIdx === 0;
  const qualifyActive = stageIdx === 1;
  const qfActive      = stageIdx === 2;
  const sfActive      = stageIdx === 3;
  const finalActive   = stageIdx === 4;

  const matchBox = (label, sub, active, special) => ({
    background: active ? (special ? 'rgba(255,215,0,0.08)' : 'rgba(157,21,21,0.18)') : '#111',
    border: `1px solid ${active ? (special ? '#FFD700' : RED) : '#1f1f1f'}`,
    padding: '8px 12px',
    fontSize: 11,
    fontFamily: "'JetBrains Mono', monospace",
    color: active ? '#fff' : 'rgba(255,255,255,0.55)',
    whiteSpace: 'nowrap',
  });
  const slotCls = (active, special) => `ovrcup-slot${active ? (special ? ' is-gold' : ' is-live') : ''}`;
  // Listed in bracket order (QF1/QF4 feed Semi A, QF2/QF3 feed Semi B) so the columns line up
  const qfOrder = [QF[0], QF[3], QF[1], QF[2]];

  return (
    <div style={{
      background: '#0d0d0d',
      border: '1px solid #1f1f1f',
      padding: 20,
      overflowX: 'auto',
    }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', minWidth: 720 }}>
        {/* Column 1: Pools */}
        {showPools && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: '0 0 150px' }}>
            <div style={{ fontSize: 10, letterSpacing: '0.18em', color: poolActive ? RED : 'rgba(255,255,255,0.35)', textTransform: 'uppercase', fontFamily: "'Bebas Neue','Oswald',sans-serif" }}>
              {poolActive ? 'POOLS · LIVE' : 'POOLS'}
            </div>
            <PoolBox letter="A" active={poolActive} qualified={qualifyActive} />
            <PoolBox letter="B" active={poolActive} qualified={qualifyActive} />
          </div>
        )}

        {/* Column 2: Quarterfinals */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '0 0 180px', justifyContent: 'center' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', color: qfActive ? RED : 'rgba(255,255,255,0.35)', textTransform: 'uppercase', fontFamily: "'Bebas Neue','Oswald',sans-serif" }}>
            QUARTERFINALS
          </div>
          {qfOrder.map((m, i) => (
            <div key={m.id} className={slotCls(qfActive)} style={{ ...matchBox(`${m.left} vs ${m.right}`, '', qfActive), transitionDelay: qfActive ? `${i * 70}ms` : '0ms' }}>
              <div style={{ fontSize: 9, opacity: 0.6, marginBottom: 2 }}>{m.id}</div>
              <div style={{ fontWeight: 600 }}>{m.left}  vs  {m.right}</div>
            </div>
          ))}
        </div>

        {/* Column 3: Semis */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28, flex: '0 0 180px', justifyContent: 'center' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', color: sfActive ? RED : 'rgba(255,255,255,0.35)', textTransform: 'uppercase', fontFamily: "'Bebas Neue','Oswald',sans-serif" }}>
            SEMIFINALS
          </div>
          <div className={slotCls(sfActive)} style={matchBox('SF A', '', sfActive)}>
            <div style={{ fontSize: 9, opacity: 0.6, marginBottom: 2 }}>SEMI A</div>
            <div style={{ fontWeight: 600 }}>W(QF1) vs W(QF4)</div>
          </div>
          <div className={slotCls(sfActive)} style={{ ...matchBox('SF B', '', sfActive), transitionDelay: sfActive ? '90ms' : '0ms' }}>
            <div style={{ fontSize: 9, opacity: 0.6, marginBottom: 2 }}>SEMI B</div>
            <div style={{ fontWeight: 600 }}>W(QF2) vs W(QF3)</div>
          </div>
        </div>

        {/* Column 4: Final */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: '0 0 180px', justifyContent: 'center' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.18em', color: finalActive ? '#FFD700' : 'rgba(255,255,255,0.35)', textTransform: 'uppercase', fontFamily: "'Bebas Neue','Oswald',sans-serif" }}>
            THE FINAL
          </div>
          <div className={slotCls(finalActive, true)} style={matchBox('FINAL', '', finalActive, true)}>
            <div style={{ fontSize: 9, opacity: 0.6, marginBottom: 2 }}>CHAMPIONSHIP</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>W(SA) vs W(SB)</div>
          </div>
          <div className={slotCls(false)} style={{ ...matchBox('BRONZE', '', false), opacity: finalActive ? 0.9 : 0.6 }}>
            <div style={{ fontSize: 9, opacity: 0.6, marginBottom: 2 }}>BRONZE</div>
            <div style={{ fontWeight: 600 }}>L(SA) vs L(SB)</div>
          </div>
          {finalActive && (
            <div style={{ textAlign: 'center', fontSize: 26, marginTop: 4 }}><span className="ovrcup-trophy">🏆</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

function PoolBox({ letter, active, qualified }) {
  const teams = ['#1', '#2', '#3', '#4', '#5'];
  return (
    <div className={`ovrcup-slot${active ? ' is-live' : ''}`} style={{
      background: active ? 'rgba(157,21,21,0.12)' : '#111',
      border: `1px solid ${active ? RED : '#1f1f1f'}`,
      padding: '10px 12px',
    }}>
      <div style={{ fontFamily: "'Bebas Neue','Oswald',sans-serif", fontSize: 18, letterSpacing: '0.08em', color: active ? '#fff' : 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
        POOL {letter}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {teams.map((t, i) => {
          const isQualifier = qualified && i < 4;
          const isEliminated = qualified && i === 4;
          return (
            <div key={i} style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              color: isQualifier ? '#7ee07e' : isEliminated ? '#666' : 'rgba(255,255,255,0.6)',
              textDecoration: isEliminated ? 'line-through' : 'none',
              transition: 'color 0.4s ease',
              transitionDelay: qualified ? `${i * 90}ms` : '0ms',
            }}>
              {t}
              {isQualifier && <span className="ovrcup-check" style={{ marginLeft: 6, animationDelay: `${i * 90}ms` }}>✓</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
